import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box } from '@ovComponents/1-primative/box/box';
import { Icon } from '@ovComponents/1-primative/icon/icon';
import { Button } from '@ovComponents/2-component/button/button';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { TextField } from '@ovComponents/2-component/textField/textField';
import { Tooltip } from '@ovComponents/2-component/tooltip/tooltip';
import type { ChangeEvent } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FLAG_CATEGORY_FILTER_OPTIONS,
  getReconFlagStateLabel,
  getReconFlagTypeLabel,
  RECON_FLAG_STATE_FILTER_VALUES,
  RECON_FLAGS_MAX_ACCOUNT_IDS,
  severityFilterAccentColor,
  sortReconFlagCategoryFilterOptionsByLabel,
  sortReconFlagStateFilterValuesByLabel,
  sortReconFlagTypesByLabel,
} from '../utils/reconFlags.config';
import {
  getReconFlagsFiltersDefaultSelectSx,
  getReconFlagsFiltersSearchFieldSx,
  getReconFlagsFiltersSearchWrapperSx,
  getReconFlagsFiltersStateSelectSx,
  reconFlagsFiltersControlsGridSx,
  reconFlagsFiltersSeverityPillsRowSx,
  reconFlagsFiltersToolbarSx,
} from './reconFlagsFilters.styles';
import type { ReconFlagsFiltersCardProps } from './reconFlagsFiltersCard.types';
import { SeverityFilterPill } from './severityFilterPill';

export const ReconFlagsFiltersCard = ({
  sys,
  severityTab,
  onSeverityTabChange,
  flagState,
  onFlagStateChange,
  flagType,
  onFlagTypeChange,
  flagTypes,
  categoryFilter,
  onCategoryFilterChange,
  agingFilter,
  onAgingFilterChange,
  search,
  onSearchChange,
  hasActiveFilters,
  onClearFilters,
}: ReconFlagsFiltersCardProps) => {
  const { t } = useTranslation(['reconciliation']);
  const sortedFlagTypes = useMemo(
    () => sortReconFlagTypesByLabel(t, flagTypes),
    [flagTypes, t],
  );
  const sortedCategoryFilterOptions = useMemo(
    () =>
      sortReconFlagCategoryFilterOptionsByLabel(
        t,
        FLAG_CATEGORY_FILTER_OPTIONS,
      ),
    [t],
  );
  const sortedStateFilterValues = useMemo(
    () =>
      sortReconFlagStateFilterValuesByLabel(t, RECON_FLAG_STATE_FILTER_VALUES),
    [t],
  );

  return (
    <>
      <Box sx={reconFlagsFiltersToolbarSx}>
        <Box sx={reconFlagsFiltersSeverityPillsRowSx}>
          <SeverityFilterPill
            label={t('reconFlags.severity.all')}
            active={severityTab === 'ALL'}
            inactiveBorderAndText={String(sys.color.outline)}
            onClick={() => onSeverityTabChange('ALL')}
            sys={sys}
          />
          <SeverityFilterPill
            label={t('reconFlags.severity.critical')}
            active={severityTab === 'CRITICAL'}
            inactiveBorderAndText={severityFilterAccentColor(sys, 'CRITICAL')}
            onClick={() => onSeverityTabChange('CRITICAL')}
            sys={sys}
          />
          <SeverityFilterPill
            label={t('reconFlags.severity.high')}
            active={severityTab === 'HIGH'}
            inactiveBorderAndText={severityFilterAccentColor(sys, 'HIGH')}
            onClick={() => onSeverityTabChange('HIGH')}
            sys={sys}
          />
          <SeverityFilterPill
            label={t('reconFlags.severity.medium')}
            active={severityTab === 'MEDIUM'}
            inactiveBorderAndText={severityFilterAccentColor(sys, 'MEDIUM')}
            onClick={() => onSeverityTabChange('MEDIUM')}
            sys={sys}
          />
          <SeverityFilterPill
            label={t('reconFlags.severity.low')}
            active={severityTab === 'LOW'}
            inactiveBorderAndText={severityFilterAccentColor(sys, 'LOW')}
            onClick={() => onSeverityTabChange('LOW')}
            sys={sys}
          />
        </Box>
        {hasActiveFilters && (
          <Button
            variant='text'
            color='primary'
            size='sm'
            label={t('reconFlags.filters.clearFilters')}
            onClick={onClearFilters}
          />
        )}
      </Box>
      <Box sx={reconFlagsFiltersControlsGridSx}>
        <TextField
          select
          size='small'
          label={t('state')}
          value={flagState}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            onFlagStateChange(e.target.value)
          }
          fullWidth
          sx={getReconFlagsFiltersStateSelectSx(sys)}
        >
          {sortedStateFilterValues.map((state) => (
            <MenuItem key={state} value={state}>
              {getReconFlagStateLabel(t, state)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size='small'
          label={t('reconFlags.filters.flagType')}
          value={flagType ?? 'any'}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            const v = e.target.value;
            onFlagTypeChange(v !== 'any' ? v : undefined);
          }}
          fullWidth
          sx={getReconFlagsFiltersDefaultSelectSx(sys)}
        >
          <MenuItem value='any'>{t('reconFlags.filters.any')}</MenuItem>
          {sortedFlagTypes.map((ft: string) => (
            <MenuItem key={ft} value={ft}>
              {getReconFlagTypeLabel(t, ft)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size='small'
          label={t('reconFlags.filters.category')}
          value={categoryFilter}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            onCategoryFilterChange(e.target.value)
          }
          fullWidth
          sx={getReconFlagsFiltersDefaultSelectSx(sys)}
        >
          <MenuItem value='any'>{t('reconFlags.filters.any')}</MenuItem>
          {sortedCategoryFilterOptions.map(({ value, i18nKey }) => (
            <MenuItem key={value} value={value}>
              {t(`reconFlags.categories.${i18nKey}`)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size='small'
          label={t('reconFlags.filters.aging')}
          value={agingFilter}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            onAgingFilterChange(e.target.value)
          }
          fullWidth
          sx={getReconFlagsFiltersDefaultSelectSx(sys)}
        >
          <MenuItem value='any'>{t('reconFlags.filters.any')}</MenuItem>
          <MenuItem value='1'>{t('reconFlags.filters.agingOneDay')}</MenuItem>
          <MenuItem value='3'>
            {t('reconFlags.filters.agingThreeDays')}
          </MenuItem>
          <MenuItem value='7'>
            {t('reconFlags.filters.agingSevenDays')}
          </MenuItem>
        </TextField>
        <Box sx={getReconFlagsFiltersSearchWrapperSx()}>
          <TextField
            size='small'
            placeholder={t('reconFlags.searchPlaceholder')}
            value={search}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              onSearchChange(e.target.value)
            }
            fullWidth
            sx={getReconFlagsFiltersSearchFieldSx(sys)}
          />
          <Tooltip
            title={t('reconFlags.searchHelp', {
              max: RECON_FLAGS_MAX_ACCOUNT_IDS,
            })}
          >
            <Box
              component='span'
              display='flex'
              alignItems='center'
              sx={{
                color: String(sys.color.onSurfaceVariant),
                cursor: 'pointer',
                flexShrink: 0,
              }}
              aria-label={t('reconFlags.searchHelpAria')}
            >
              <Icon icon={InfoOutlinedIcon} size='small' />
            </Box>
          </Tooltip>
        </Box>
      </Box>
    </>
  );
};
